import {useEffect, useState} from 'react';
import {useTraduire} from '../i18n/langue';
import {T} from '../i18n/textes';
import {doc, getDoc, onSnapshot} from 'firebase/firestore';
import {db} from '../services/firebase';
import {
  AiAnalysis,
  AiRedactSuggestion,
  AiSimilarFeedback,
  FeedbackReport,
} from '../types/feedback';
import {Link} from 'react-router-dom';
import {feedbackPath} from '../routes';

const REASON_LABELS: Record<string, { label: string; color: string }> = {
  pii: {label: 'PII', color: 'bg-red-100 text-red-700'},
  device: {label: 'Device', color: 'bg-blue-100 text-blue-700'},
  aggressive: {label: 'Aggressive', color: 'bg-orange-100 text-orange-700'},
  irrelevant: {label: 'Irrelevant', color: 'bg-gray-100 text-gray-600'},
};

function useAiAnalysis(feedbackId: string, initialAnalysis?: AiAnalysis, shouldWait = true) {
  const [analysis, setAnalysis] = useState<AiAnalysis | null>(initialAnalysis ?? null);
  const [loading, setLoading] = useState(!initialAnalysis && shouldWait);

  useEffect(() => {
    if (initialAnalysis || !shouldWait) return;

    const docRef = doc(db, 'feedback', feedbackId);
    const unsubscribe = onSnapshot(docRef, (snap) => {
      const data = snap.data();
      if (data?.aiAnalysis) {
        setAnalysis(data.aiAnalysis as AiAnalysis);
        setLoading(false);
        unsubscribe();
      }
    });

    // Auto-stop after 30 seconds
    const timeout = setTimeout(() => {
      unsubscribe();
      setLoading(false);
    }, 30000);

    return () => {
      unsubscribe();
      clearTimeout(timeout);
    };
  }, [feedbackId, initialAnalysis]);

  return {analysis, loading};
}

function ConfidenceBar({confidence}: { confidence: number }) {
  const pct = Math.round(confidence * 100);
  const color = pct >= 80 ? 'bg-green-500' : pct >= 60 ? 'bg-yellow-400' : 'bg-gray-300';
  const textColor = pct >= 80 ? 'text-green-600' : pct >= 60 ? 'text-yellow-600' : 'text-gray-500';
  return (
    <div className="flex items-center gap-2">
      <div className="h-1 w-16 rounded-full bg-gray-200 overflow-hidden">
        <div className={`h-full rounded-full ${color}`} style={{width: `${pct}%`}}/>
      </div>
      <span className={`text-xs font-semibold ${textColor}`}>{pct}%</span>
    </div>
  );
}

function RedactChip({suggestion}: { suggestion: AiRedactSuggestion }) {
  const reason = REASON_LABELS[suggestion.reason] || REASON_LABELS.irrelevant;
  const isMandatory = suggestion.severity === 'mandatory';
  return (
    <div className="flex items-center gap-2 text-xs">
      <span className={`inline-flex px-1.5 py-0.5 text-[10px] font-semibold rounded ${
        isMandatory ? 'bg-red-600 text-white' : 'bg-amber-100 text-amber-800'
      }`}>
        {isMandatory ? 'MANDATORY' : 'SUGGESTED'}
      </span>
      <span className="text-gray-700 truncate max-w-[300px]">"{suggestion.text}"</span>
      <span className={`inline-flex px-1.5 py-0.5 text-[10px] font-medium rounded-full ${reason.color}`}>
        {reason.label}
      </span>
    </div>
  );
}

function SimilarItem({item}: { item: AiSimilarFeedback }) {
    const t = useTraduire();
  const [showPopup, setShowPopup] = useState(false);
  const [feedbackMessage, setFeedbackMessage] = useState<string | null>(null);

  const loadMessage = async () => {
    if (feedbackMessage !== null) {
      setShowPopup(true);
      return;
    }
    try {
      const snap = await getDoc(doc(db, 'feedback', item.feedbackId));
      setFeedbackMessage(snap.exists() ? (snap.data().message as string || '') : '(not found)');
      setShowPopup(true);
    } catch {
      setShowPopup(true);
      setFeedbackMessage('(error loading)');
    }
  };

  return (
    <>
      <div
        className="flex items-center justify-between p-2.5 rounded-lg border border-gray-100 bg-gray-50 hover:bg-gray-100 cursor-pointer transition-colors"
        onClick={loadMessage}
      >
        <div className="flex items-center gap-3 min-w-0">
          <span className="text-sm font-medium text-primary truncate">
            {item.feedbackId.substring(0, 12)}...
          </span>
          <span className="text-xs text-gray-400 whitespace-nowrap">
            re: {item.matchedTopic}
          </span>
        </div>
        <div className="flex items-center gap-3 flex-shrink-0">
          <ConfidenceBar confidence={item.confidence}/>
          <Link
            to={feedbackPath(item.feedbackId)}
            className="text-xs text-primary hover:underline"
            onClick={e => e.stopPropagation()}
          >
            View
          </Link>
        </div>
      </div>

      {/* Popup with message + link options */}
      {showPopup && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
          <div className="fixed inset-0 bg-black/20" onClick={() => setShowPopup(false)}/>
          <div className="relative bg-white rounded-xl shadow-lg max-w-lg w-full p-6" style={{animation: 'fadeIn 0.15s ease-out'}}>
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-sm font-semibold text-gray-800">{t(T.demandesSimilaires)}</h3>
              <button onClick={() => setShowPopup(false)} className="text-gray-400 hover:text-gray-600">
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12"/>
                </svg>
              </button>
            </div>

            {/* Message */}
            <div className="bg-gray-50 rounded-lg p-4 mb-4 max-h-40 overflow-y-auto">
              <p className="text-sm text-gray-700 whitespace-pre-wrap">
                {feedbackMessage ?? 'Loading...'}
              </p>
            </div>

            {/* Confidence */}
            <div className="flex items-center gap-2 mb-4">
              <span className="text-xs text-gray-500">{t(T.confiance)}</span>
              <ConfidenceBar confidence={item.confidence}/>
              <span className="text-xs text-gray-500">re: {item.matchedTopic}</span>
            </div>

          </div>
        </div>
      )}

      <style>{`@keyframes fadeIn { from { opacity: 0; transform: translateY(4px); } to { opacity: 1; transform: translateY(0); } }`}</style>
    </>
  );
}

interface AiAnalysisPanelProps {
  feedback: FeedbackReport;
}

// Only show the loading spinner for feedbacks created in the last 5 minutes
// (older feedbacks were created before the AI analysis feature existed)
function isRecentFeedback(feedback: FeedbackReport): boolean {
  if (!feedback.createdAt?.toDate) return false;
  const fiveMinutesAgo = Date.now() - 5 * 60 * 1000;
  return feedback.createdAt.toDate().getTime() > fiveMinutesAgo;
}

export function AiAnalysisPanel({feedback}: AiAnalysisPanelProps) {
    const t = useTraduire();
  const shouldWaitForAnalysis = !feedback.aiAnalysis && isRecentFeedback(feedback);
  const {analysis, loading} = useAiAnalysis(
    feedback.id,
    feedback.aiAnalysis,
    shouldWaitForAnalysis,
  );

  // Don't render anything if there's no analysis and we're not waiting for one
  if (!analysis && !loading) {
    return null;
  }

  return (
    <div className="bg-white rounded-lg shadow p-6 border-l-4 border-violet-400">
      {/* Header */}
      <div className="flex items-center gap-2.5 mb-5">
        <h3 className="text-lg font-medium text-gray-900">{t(T.analyseIA)}</h3>
        <span className="inline-flex items-center gap-1 px-2 py-0.5 text-[10px] font-medium rounded-full bg-violet-100 text-violet-700">
          <svg className="w-2.5 h-2.5" viewBox="0 0 24 24" fill="none" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z"/>
          </svg>
          Claude AI
        </span>
      </div>

      {/* Loading state */}
      {loading && (
        <div className="flex items-center gap-2 text-gray-500">
          <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-violet-400"/>
          <span className="text-sm">{t(T.analyseEnCours)}</span>
        </div>
      )}

      {analysis && (
        <div className="space-y-5">
          {/* Topics */}
          {analysis.topics.length > 0 && (
            <div>
              <p className="text-xs font-medium text-gray-500 uppercase tracking-wider mb-2">
                Topics detected
              </p>
              <div className="flex gap-2 flex-wrap">
                {analysis.topics.map((topic, i) => (
                  <span
                    key={i}
                    className="inline-flex px-3 py-1 text-sm font-medium rounded-full bg-violet-50 text-violet-700"
                  >
                    {topic}
                  </span>
                ))}
              </div>
            </div>
          )}

          {/* Similar feedbacks */}
          {analysis.similarFeedbacks.length > 0 && (
            <div>
              <p className="text-xs font-medium text-gray-500 uppercase tracking-wider mb-2">
                Similar feedbacks ({analysis.similarFeedbacks.length})
              </p>
              <div className="space-y-1.5">
                {analysis.similarFeedbacks.map((item, i) => (
                  <SimilarItem key={i} item={item}/>
                ))}
              </div>
            </div>
          )}

          {analysis.similarFeedbacks.length === 0 && (
            <p className="text-sm text-gray-400 italic">{t(T.aucuneDemandeSimilaire)}</p>
          )}

          {/* Redaction suggestions */}
          {analysis.redactSuggestions.length > 0 && (
            <div>
              <p className="text-xs font-medium text-gray-500 uppercase tracking-wider mb-2">
                Redaction suggestions ({analysis.redactSuggestions.length})
              </p>
              <div className="space-y-1.5">
                {analysis.redactSuggestions.map((suggestion, i) => (
                  <RedactChip key={i} suggestion={suggestion}/>
                ))}
              </div>
            </div>
          )}

          {/* Model info */}
          <p className="text-[10px] text-gray-300 text-right">
            {analysis.model}
          </p>
        </div>
      )}
    </div>
  );
}
